import { useState, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { AppStore } from "../../../redux/store";
import { get, find, map, filter, last } from "lodash";
import { GadmFeature } from "../components/Map/types";
import { HeatMapPolygon, HeatMapV2Data } from "../models/heatMapModel";
import { v2PolygonsEndpoint } from "../services/heatMapService";
import { loadGeoJson } from "../components/Map/geoJsonLoader";
import { fetchResponseAdapter } from "../../../adapters/fetchAdapter";
import useCallApiAndLoad from "../../../hooks/useCallApiAndLoad";

const toFeature = (polygon: HeatMapPolygon): GadmFeature => ({
  type: "Feature",
  geometry: {
    type: polygon.geo_points.type as "Polygon" | "MultiPolygon",
    coordinates: polygon.geo_points.coordinates,
  },
  properties: {
    GID_0: "",
    [`NAME_${polygon.level}`]: polygon.name,
    id: polygon.id,
    ppm2_sale: polygon.ppm2_sale,
    currency: polygon.currency,
  },
});

interface PolygonsState {
  features: GadmFeature[];
  backgroundFeatures: GadmFeature[];
  level: number;
  parentId: number | null;
  loading: boolean;
  data: HeatMapV2Data | null;
  trail: Array<{ level: number; id: number; name: string }>;
}

const usePolygons = () => {
  const country = useSelector((store: AppStore) => get(store, "country.country", "sv"));
  const [searchParams, setSearchParams] = useSearchParams();
  const skipUrlSync = useRef(false);
  const isFirstRender = useRef(true);

  const { callEndpoint } = useCallApiAndLoad();
  const callRef = useRef(callEndpoint);
  callRef.current = callEndpoint;

  const [priceType, setPriceType] = useState("sale");
  const [priceMin, setPriceMin] = useState<number | undefined>();
  const [priceMax, setPriceMax] = useState<number | undefined>();

  const [state, setState] = useState<PolygonsState>({
    features: [],
    backgroundFeatures: [],
    level: 1,
    parentId: null,
    loading: false,
    data: null,
    trail: [],
  });

  const fetchPolygons = useCallback(async (params: { level?: number; parent?: number }): Promise<HeatMapV2Data | null> => {
    // TODO: reactivar min_price y max_price cuando se integre el filtro por precio
    const response = await callRef.current(v2PolygonsEndpoint({ ...params, price_type: priceType }));
    const adapted = fetchResponseAdapter(get(response, "data"));
    return get(adapted, "code") === 0 ? get(adapted, "data") : null;
  }, [priceType]);

  const loadByLevel = useCallback(async (level: number) => {
    setState(prev => ({ ...prev, loading: true }));
    const data = await fetchPolygons({ level });
    if (data) {
      setState(prev => ({
        ...prev,
        features: map(get(data, "polygons"), toFeature),
        level,
        parentId: null,
        loading: false,
        data,
        trail: filter(prev.trail, item => item.level < level),
      }));
    } else {
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [fetchPolygons]);

  const restoreFromTrail = useCallback(async (trailIds: string[]) => {
    skipUrlSync.current = true;
    setState(prev => ({ ...prev, loading: true }));
    try {
      const trail: PolygonsState["trail"] = [];
      for (let i = 0; i < trailIds.length; i++) {
        const id = Number(trailIds[i]);
        const params = i === 0 ? { level: 1 } : { level: i + 1, parent: trail[i - 1].id };
        const data = await fetchPolygons(params);
        const polygon = find(get(data, "polygons"), { id });
        trail.push({ level: i + 1, id, name: get(polygon, "name", String(id)) });
      }
      const lastId = get(last(trail), "id") as number;
      const finalLevel = trailIds.length + 1;
      const finalData = await fetchPolygons({ level: finalLevel, parent: lastId });
      if (finalData) {
        setState({
          features: map(get(finalData, "polygons"), toFeature),
          backgroundFeatures: [],
          level: finalLevel,
          parentId: lastId,
          loading: false,
          data: finalData,
          trail,
        });
      }
    } catch {
      setState(prev => ({ ...prev, loading: false }));
      loadByLevel(1);
    } finally {
      skipUrlSync.current = false;
    }
  }, [fetchPolygons, loadByLevel]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      const trailParam = searchParams.get("trail");
      if (trailParam) {
        restoreFromTrail(trailParam.split(","));
        return;
      }
    }
    loadByLevel(1);
  }, [country]);

  // Carga polígonos de fondo desde GeoJSON local según el nivel activo:
  // - Level 1: 0.json + 1.json (silueta país + departamentos)
  // - Level 2: 0.json + 1.json + 2.json (+ municipios)
  // - Level 3+: 0.json + 1.json + 2.json + 3.json (+ distritos)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const layers = [loadGeoJson(country, 0), loadGeoJson(country, 1)];
      if (state.level >= 2) layers.push(loadGeoJson(country, 2));
      if (state.level >= 3) layers.push(loadGeoJson(country, 3));
      const results = await Promise.all(layers);
      if (!cancelled) setState(prev => ({ ...prev, backgroundFeatures: results.flat() }));
    };
    load();
    return () => { cancelled = true; };
  }, [state.level, country]);

  // Inicializa precios con los valores de la API al primer fetch
  useEffect(() => {
    if (!state.data) return;
    const minRange = get(state.data, "min_price_range_value");
    const maxRange = get(state.data, "max_price_range_value");
    if (priceMin === undefined && minRange) setPriceMin(minRange);
    if (priceMax === undefined && maxRange) setPriceMax(maxRange);
  }, [state.data]);

  // Sincroniza trail → URL (push al historial para que back/forward funcionen)
  useEffect(() => {
    if (skipUrlSync.current) return;
    const trailIds = map(state.trail, t => String(t.id)).join(",");
    setSearchParams(trailIds ? { trail: trailIds } : {});
  }, [state.trail]);

  // Escucha navegación con botón atrás/adelante del navegador y restaura el trail
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const trailParam = get(params, "trail") || params.get("trail");
      if (trailParam) {
        restoreFromTrail(trailParam.split(","));
      } else {
        loadByLevel(1);
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [restoreFromTrail, loadByLevel]);

  const loadByParent = useCallback(async (parentId: number, level: number, parentName?: string) => {
    // Si el nivel actual no permite buscar más profundo, no consumir el siguiente nivel
    if (!get(state.data, "allowed_to_search", true)) return;
    setState(prev => ({ ...prev, loading: true }));
    const data = await fetchPolygons({ level, parent: parentId });
    if (data) {
      setState(prev => {
        const baseTrail = filter(prev.trail, item => item.level < level - 1);
        const trail = parentName
          ? [...baseTrail, { level: level - 1, id: parentId, name: parentName }]
          : baseTrail;
        return { ...prev, features: map(get(data, "polygons"), toFeature), level, parentId, loading: false, data, trail };
      });
    } else {
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [fetchPolygons, state.data]);

  const navigateToLevel = useCallback(async (targetLevel: number) => {
    if (targetLevel <= 1) {
      await loadByLevel(1);
      return;
    }
    const parentNode = find(state.trail, item => item.level === targetLevel - 1);
    if (!parentNode) {
      await loadByLevel(1);
      return;
    }
    setState(prev => ({ ...prev, loading: true }));
    const data = await fetchPolygons({ level: targetLevel, parent: get(parentNode, "id") });
    if (data) {
      setState(prev => ({
        ...prev,
        features: map(get(data, "polygons"), toFeature),
        level: targetLevel,
        parentId: get(parentNode, "id"),
        loading: false,
        data,
        trail: filter(prev.trail, item => item.level < targetLevel),
      }));
    } else {
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [fetchPolygons, loadByLevel, state.trail]);

  return {
    features: state.features,
    backgroundFeatures: state.backgroundFeatures,
    level: state.level,
    parentId: state.parentId,
    loading: state.loading,
    data: state.data,
    trail: state.trail,
    priceType,
    setPriceType,
    priceMin,
    priceMax,
    setPriceMin,
    setPriceMax,
    loadByLevel,
    loadByParent,
    navigateToLevel,
  };
};

export default usePolygons;
